"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { ContentListBlock, SectionHeader } from "fansunited-sports-ui";
import type { ContentListItem } from "fansunited-sports-ui";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import type { Post } from "@/payload-types";
const MAX_ITEMS = 4;
export interface LivescoreHomeNewsGridProps {
  /** Editorially curated featured posts (from Lists collection). May be empty. */
  featuredPosts: Post[];
  /** General latest posts to use when no curated list is configured. */
  latestPosts: Post[];
}

/**
 * News row for the livescore-focused home variant.
 *
 * Renders a 4-across grid of image-over-title cards below the livescore body.
 * Prefers curated `featuredPosts`; falls back to `latestPosts` when the curated
 * list is empty. Caps at four items to match the wireframe.
 *
 * Renders nothing when neither list has content.
 */
export function LivescoreHomeNewsGrid(t0) {
  const $ = _c(11);
  const {
    featuredPosts,
    latestPosts
  } = t0;
  const nav = useNavigation();
  const labels = useLabels();
  const posts = featuredPosts.length > 0 ? featuredPosts : latestPosts;
  let t1;
  if ($[0] !== nav || $[1] !== posts) {
    let t2;
    if ($[3] !== nav) {
      t2 = post => ({
        content: {
          id: String(post.id),
          type: "article" as const,
          link: nav.post(post),
          title: post.title,
          image: {
            url: getPostImageUrl(post, "medium"),
            alt: post.title
          }
        }
      });
      $[3] = nav;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = posts.slice(0, MAX_ITEMS).map(t2);
    $[0] = nav;
    $[1] = posts;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const items = t1;
  if (items.length === 0) {
    return null;
  }
  let t2;
  if ($[5] !== labels.homePage.latestNews) {
    t2 = <div className="mb-2"><SectionHeader entity={{
        name: labels.homePage.latestNews
      }} /></div>;
    $[5] = labels.homePage.latestNews;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      columns: {
        sm: 1,
        md: 2,
        lg: 4
      },
      gap: "md",
      borderRadius: "none"
    };
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== items || $[9] !== t2) {
    t4 = <ContentListBlock variant="grid" sectionHeader={t2} items={items} cardVariant="standard" styles={t3} />;
    $[8] = items;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}